import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useUsers } from "./hooks/useUsers";
import { showToast } from "../../../../core/utils/alert";
import { useChangePasswordMutation } from "./api/usersApi";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";

interface UserModalsProps {
  userId?: string;
  centerId?: number;
  username?: string;
  show?: {
    delete: boolean;
    changePassword: boolean;
    deactivate: boolean;
    activate: boolean;
  };
  onHide?: () => void;
  onSuccess?: () => void;
}

const UserModals: React.FC<UserModalsProps> = ({
  userId,
  centerId,
  username,
  show,
  onHide,
  onSuccess,
}) => {
  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();
  const { deleteUser, isDeleting, deactivateUser, activateUser } = useUsers({
    centerId,
  });

  // Add state for password change form
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Add state for validation errors
  const [validationErrors, setValidationErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    matchError: "",
  });

  // Add state for password visibility
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // Toggle password visibility
  const togglePasswordVisibility = (field: string) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev],
    }));
  };

  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation errors when user types
    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
      matchError:
        name === "confirmPassword" || name === "newPassword"
          ? ""
          : prev.matchError,
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      matchError: "",
    };

    // Check for empty fields
    if (!passwordData.oldPassword) {
      errors.oldPassword = "Current password is required";
      isValid = false;
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
      isValid = false;
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
      isValid = false;
    }

    // Check if passwords match
    if (
      passwordData.newPassword &&
      passwordData.confirmPassword &&
      passwordData.newPassword !== passwordData.confirmPassword
    ) {
      errors.matchError = "New password and confirm password do not match";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      if (!userId) return;

      await changePassword({
        id: parseInt(userId),
        payload: {
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
        },
      }).unwrap();

      showToast({
        title: "Success",
        text: "Password changed successfully",
        icon: "success",
        timer: 5000,
      });
    } catch (error: any) {
      // Extract the specific error message from the API response
      const errorMessage = getApiErrorMessage(error) || "Failed to change password";

      showToast({
        title: "Error",
        text: errorMessage,
        icon: "error",
        timer: 5000,
      });
    } finally {
      // Always reset password fields
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Clear validation errors
      setValidationErrors({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
        matchError: "",
      });

      // Reset password visibility
      setPasswordVisibility({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
      });

      // Always close modal and trigger the success callback which will handle navigation
      // This will happen regardless of success or failure
      onHide?.();
      onSuccess?.();
    }
  };

  const handleDelete = async () => {
    try {
      if (!userId) return;

      await deleteUser(parseInt(userId));
      showToast({
        title: "Success",
        text: "User deleted successfully",
        icon: "success",
        timer: 5000,
      });
      onHide?.();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error) || "Failed to delete user";
      showToast({
        title: "Error",
        text: errorMessage,
        icon: "error",
        timer: 5000,
      });
    }
  };

  const handleDeactivate = async () => {
    try {
      if (!userId) return;

      await deactivateUser(parseInt(userId));
      showToast({
        title: "Success",
        text: "User deactivated successfully",
        icon: "success",
        timer: 5000,
      });
      onHide?.();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error) || "Failed to deactivate user";
      showToast({
        title: "Error",
        text: errorMessage,
        icon: "error",
        timer: 5000,
      });
    }
  };

  const handleActivate = async () => {
    try {
      if (!userId) return;

      await activateUser(parseInt(userId));
      showToast({
        title: "Success",
        text: "User activated successfully",
        icon: "success",
        timer: 5000,
      });
      onHide?.();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error) || "Failed to activate user";
      showToast({
        title: "Error",
        text: errorMessage,
        icon: "error",
        timer: 5000,
      });
    }
  };

  return (
    <>
      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleDelete();
              }}
            >
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>
                  You want to delete the user, this can't be undone once you
                  delete.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                    onClick={onHide}
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes, Delete"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <div className="modal fade" id="change-password-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Change Password{" "}
                {username && (
                  <span className="ms-1 badge bg-light text-dark">
                    {username}
                  </span>
                )}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={onHide}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <div className="position-relative">
                    <input
                      type={
                        passwordVisibility.oldPassword ? "text" : "password"
                      }
                      className={`form-control ${
                        validationErrors.oldPassword ? "is-invalid" : ""
                      }`}
                      name="oldPassword"
                      value={passwordData.oldPassword}
                      onChange={handlePasswordInputChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => togglePasswordVisibility("oldPassword")}
                    >
                      <i
                        className={`ti ${
                          passwordVisibility.oldPassword
                            ? "ti-eye-off"
                            : "ti-eye"
                        }`}
                      ></i>
                    </span>
                  </div>
                  {validationErrors.oldPassword && (
                    <div className="text-danger small mt-1">
                      {validationErrors.oldPassword}
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <div className="position-relative">
                    <input
                      type={
                        passwordVisibility.newPassword ? "text" : "password"
                      }
                      className={`form-control ${
                        validationErrors.newPassword ? "is-invalid" : ""
                      }`}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordInputChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => togglePasswordVisibility("newPassword")}
                    >
                      <i
                        className={`ti ${
                          passwordVisibility.newPassword
                            ? "ti-eye-off"
                            : "ti-eye"
                        }`}
                      ></i>
                    </span>
                  </div>
                  {validationErrors.newPassword && (
                    <div className="text-danger small mt-1">
                      {validationErrors.newPassword}
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm New Password</label>
                  <div className="position-relative">
                    <input
                      type={
                        passwordVisibility.confirmPassword ? "text" : "password"
                      }
                      className={`form-control ${
                        validationErrors.confirmPassword ||
                        validationErrors.matchError
                          ? "is-invalid"
                          : ""
                      }`}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordInputChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        togglePasswordVisibility("confirmPassword")
                      }
                    >
                      <i
                        className={`ti ${
                          passwordVisibility.confirmPassword
                            ? "ti-eye-off"
                            : "ti-eye"
                        }`}
                      ></i>
                    </span>
                  </div>
                  {validationErrors.confirmPassword && (
                    <div className="text-danger small mt-1">
                      {validationErrors.confirmPassword}
                    </div>
                  )}
                </div>

                {validationErrors.matchError && (
                  <div className="alert alert-danger py-2">
                    {validationErrors.matchError}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={onHide}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Modal */}
      <div className="modal fade" id="deactivate-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleDeactivate();
              }}
            >
              <div className="modal-body text-center p-4">
                <div className="warning-icon mb-4">
                  <i
                    className="ti ti-alert-triangle"
                    style={{ fontSize: "4rem", color: "#ffc107" }}
                  />
                </div>
                <h4 className="mb-3 fw-semibold">Confirm Deactivation</h4>
                <p className="text-muted mb-4">
                  You want to deactivate the user. Once deactivated, the user
                  will not be able to login to the system.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                    onClick={onHide}
                  >
                    Cancel
                  </Link>
                  <button type="submit" className="btn btn-warning px-4">
                    Yes, Deactivate
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Activate Modal */}
      <div className="modal fade" id="activate-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleActivate();
              }}
            >
              <div className="modal-body text-center p-4">
                <div className="success-icon mb-4">
                  <i
                    className="ti ti-user-check"
                    style={{ fontSize: "4rem", color: "#198754" }}
                  />
                </div>
                <h4 className="mb-3 fw-semibold">Confirm Activation</h4>
                <p className="text-muted mb-4">
                  You want to activate the user. Once activated, the user will
                  regain access to the system and be able to login again.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                    onClick={onHide}
                  >
                    Cancel
                  </Link>
                  <button type="submit" className="btn btn-success px-4">
                    Yes, Activate
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserModals;
